import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    getAllProducts,
    BASE_URL,
} from "../services/productService";
import ProductItem from "../components/ProductItem";
import HorizontalCategoryList from "../components/HorizontalCategoryList";
import TopMenu from "../components/TopMenu";
import BottomMenu from "../components/BottomMenu";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ScrollSpacer = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY || 0;
            const should = y > 50; // was 100 — align with HorizontalCategoryList
            if (should !== isScrolled) setIsScrolled(should);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, [isScrolled]);
    return <Spacer $isVisible={isScrolled} />;
};

const ProductsScreen = () => {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const [selectedFridge, setSelectedFridge] = useState(() => localStorage.getItem("selectedFridgeId") || null);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const { cartItems, setCartItems } = useCart();
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [availableQuantities, setAvailableQuantities] = useState({});
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // Track selected team player when voucher fridge is active
    const [selectedTeamPlayerId, setSelectedTeamPlayerId] = useState(null);

    // Voucher fridge flag state
    const [isVoucherFridge, setIsVoucherFridge] = useState(() => {
        try {
            const raw = localStorage.getItem("isVoucherFridge");
            return raw ? !!JSON.parse(raw) : false;
        } catch { return false; }
    });

    // Sync voucher flag from backend when fridge changes
    useEffect(() => {
        let cancelled = false;
        const fetchFlag = async () => {
            if (!selectedFridge) return;
            try {
                const res = await fetch(`${BASE_URL}/fridge/${selectedFridge}/voucher-flag`);
                if (!res.ok) return;
                const data = await res.json();
                const flag = !!data?.isVoucherFridge;
                if (!cancelled) {
                    setIsVoucherFridge(flag);
                    try { localStorage.setItem("isVoucherFridge", JSON.stringify(flag)); } catch {}
                }
            } catch {}
        };
        fetchFlag();
        return () => { cancelled = true; };
    }, [selectedFridge]);

    // Reset selection when fridge or voucher mode changes
    useEffect(() => {
        setSelectedCategory("all");
        setSelectedTeamPlayerId(null);
    }, [selectedFridge, isVoucherFridge]);

    const applyFilters = (productList, category, search) => {
        let filtered = productList;
        if (category !== "all") {
            filtered = filtered.filter((p) => p.categoryId?.toString() === category);
        }
        if (search.trim() !== "") {
            const q = search.toLowerCase().trim();
            filtered = filtered.filter(
                (p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
            );
        }
        setFilteredProducts(filtered);
    };

    useEffect(() => {
        if (selectedFridge) {
            setLoading(true);
            localStorage.setItem("selectedFridgeId", selectedFridge);
            const options = (isVoucherFridge && selectedTeamPlayerId)
                ? { teamPlayerId: selectedTeamPlayerId }
                : undefined;
            getAllProducts(selectedFridge, options)
                .then((productsFromAPI) => {
                    const adaptedProducts = productsFromAPI.map((product) => ({
                        id: product.id,
                        title: product.productName,
                        imageURL: product.imageUrl,
                        price: product.customSalesPrice || product.defaultSalesPrice,
                        description: product.description,
                        categoryId: product.categoryId,
                        quantity: 1,
                        availableQuantity: product.quantity,
                        isVoucher: product.isVoucher || false,
                    }));

                    const initialSelectedQuantities = {};
                    const initialAvailableQuantities = {};
                    adaptedProducts.forEach((product) => {
                        initialSelectedQuantities[product.id] = 1;
                        initialAvailableQuantities[product.id] = product.availableQuantity;
                    });

                    setProducts(adaptedProducts);
                    setFilteredProducts(adaptedProducts);
                    setSelectedQuantities(initialSelectedQuantities);
                    setAvailableQuantities(initialAvailableQuantities);
                })
                .catch((error) => {
                    console.error("Failed to fetch products", error);
                    alert("Error: Failed to fetch products.");
                })
                .finally(() => setLoading(false));
        }
    }, [selectedFridge, isVoucherFridge, selectedTeamPlayerId]);

    useEffect(() => {
        if (products.length > 0) {
            applyFilters(products, selectedCategory, searchQuery);
        }
    }, [products, selectedCategory, searchQuery]);

    if (!token) return <CenteredView>Loading...</CenteredView>;

    const handleFridgeChange = (fridgeId) => {
        setSelectedFridge(fridgeId);
        localStorage.setItem("selectedFridgeId", fridgeId);
    };

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        applyFilters(products, categoryId, searchQuery);
    };

    // In voucher mode, handle player selection coming from HorizontalCategoryList
    const handleTeamPlayerSelect = (playerId) => {
        setSelectedTeamPlayerId(playerId);
        // Ensure category filtering is neutralized while selecting a player
        if (playerId) setSelectedCategory("all");
    };

    const handleProductSearch = (query) => {
        setSearchQuery(query);
        applyFilters(products, selectedCategory, query);
    };

    const updateProductQuantity = (productId, newQuantity) => {
        setSelectedQuantities((prev) => ({ ...prev, [productId]: newQuantity }));
    };

    const handleAddToCart = (product, quantityToAdd) => {
        if (quantityToAdd > selectedQuantities[product.id]) {
            alert(`Cannot add more than selected quantity. Selected: ${selectedQuantities[product.id]}`);
            return;
        }
        const itemInCart = cartItems.find((i) => i.id === product.id);
        if (itemInCart) {
            setCartItems((prev) =>
                prev.map((i) => (i.id === product.id ? { ...i, quantity: i.quantity + quantityToAdd } : i))
            );
        } else {
            setCartItems((prev) => [...prev, { ...product, quantity: quantityToAdd }]);
        }
    };

    const handleInfoClick = (description) => {
        setModalContent(description);
        setShowModal(true);
    };
    const handleCloseModal = () => setShowModal(false);

    return (
        <ScreenContainer>
            <TopMenu onFridgeSelect={handleFridgeChange} />

            <HorizontalCategoryList
                selectedFridgeId={selectedFridge}
                onCategorySelect={handleCategorySelect}
                onProductSearch={handleProductSearch}
                isVoucherFridge={isVoucherFridge}
                onTeamPlayerSelect={handleTeamPlayerSelect}
            />

            <ScrollSpacer />

            <ScrollableContent>
                {loading ? (
                    <CenteredView>Loading products...</CenteredView>
                ) : filteredProducts.length === 0 ? (
                    <CenteredView>No products available. Please select the fridge from the list</CenteredView>
                ) : (
                    <ProductList>
                        {filteredProducts.map((item) => (
                            <ProductCard key={item.id}>
                                <ProductItem
                                    product={item}
                                    selectedQuantity={selectedQuantities[item.id]}
                                    availableQuantity={availableQuantities[item.id]}
                                    onQuantityChange={(q) => updateProductQuantity(item.id, q)}
                                    onAddToCart={() => handleAddToCart(item, selectedQuantities[item.id])}
                                    onInfoClick={() => handleInfoClick(item.description)}
                                />
                            </ProductCard>
                        ))}
                    </ProductList>
                )}
            </ScrollableContent>

            {showModal && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={handleCloseModal}>×</CloseButton>
                        <ModalText>{modalContent}</ModalText>
                    </ModalContent>
                </ModalOverlay>
            )}

            <BottomMenuSpacer />
            <BottomMenuContainer>
                <BottomMenu selectedFridgeId={selectedFridge} />
            </BottomMenuContainer>
        </ScreenContainer>
    );
};

export default ProductsScreen;

const ScreenContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: #fff;
`;

const ScrollableContent = styled.div`
    flex: 1;
    background-color: #fff;
    padding: 10px;
    margin-top: 10px;
    box-sizing: border-box;
    position: relative;
`;

const CenteredView = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #ffffff;
    font-size: 18px;
    text-align: center;
    margin-top: 40px;
`;

const ProductList = styled.div`
    margin-top: 20px;
    display: grid;
    gap: 12px; 
    padding: 0 10px;
    width: 100%;
    box-sizing: border-box;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 576px) {
        gap: 14px;
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
    @media (min-width: 768px) {
        gap: 14px;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }
    @media (min-width: 1024px) {
        gap: 16px;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
    @media (min-width: 1440px) {
        gap: 16px;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    }
`;

const ProductCard = styled.div`
    display: block;        
    max-width: 100%;
`;

const BottomMenuContainer = styled.div`
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
`;

const BottomMenuSpacer = styled.div`
    height: 70px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #fff;
    padding: 16px;
    border-radius: 10px;
    max-width: 400px;
    width: 90%;
    text-align: center;
    color: #000;
    position: relative;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: 19.2px;
    cursor: pointer;
`;

const ModalText = styled.p`
    font-size: 14px;
    line-height: 1.5;
`;

const Spacer = styled.div`
    height: ${({ $isVisible }) => ($isVisible ? "71px" : "0px")};
    transition: height 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;
